import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import type {
  ProfileResponse,
  ProfileUser,
  UpdateProfileRequest,
  UserProfile,
} from '../../types';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly api = inject(ApiService);

  private readonly _profile = signal<UserProfile | null>(null);
  readonly profile = this._profile.asReadonly();

  getProfile(): Observable<UserProfile> {
    return this.api.get<ProfileResponse>('profile', { resource: 'auth-api' }).pipe(
      map((response) => this.toProfile(response.user)),
      tap((profile) => this._profile.set(profile)),
    );
  }

  /** `PATCH profile` on auth-api. Returns the saved profile, which replaces the cached one. */
  updateProfile(request: UpdateProfileRequest): Observable<UserProfile> {
    return this.api.patch<ProfileResponse>('profile', request, { resource: 'auth-api' }).pipe(
      map((response) => this.toProfile(response.user)),
      tap((profile) => this._profile.set(profile)),
    );
  }

  clear(): void {
    this._profile.set(null);
  }

  private toProfile(user: ProfileUser): UserProfile {
    return {
      id: user.id,
      cognitoSub: user.cognito_sub,
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      jobTitle: user.job_title ?? null,
      phone: user.phone ?? null,
      avatarUrl: user.avatar_url ?? null,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
    };
  }
}
